import React, { useState } from "react";

import TrdxPurchaseModal from "./TrdxPurchaseModal";
import { useFundWalletEth } from "./useFundWalletEth";
import { useTrdxSaleSnapshot } from "../../hooks/useTrdxSaleSnapshot";
import { getTrdxNetworkLabel } from "../../services/web3/trdxSaleService";
import {
  formatEthRateLabel,
  formatSaleInventoryLabel,
  formatUsdcPriceLabel,
} from "../../services/web3/trdxSaleDisplay";
import { trackTrdxBuyClick } from "../../utils/analytics";
import "./treydorex-access-section.css";

const PANEL_TITLE = "Treydorex (TRDX)";

const TreydorexAccessSection = () => {
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const sale = useTrdxSaleSnapshot({ refreshMs: 60_000 });
  const fundWallet = useFundWalletEth();

  const treydorexSalesBase = `${(process.env.PUBLIC_URL || "").replace(/\/$/, "")}/assets/treydorex-sales`;
  const sectionBgLayers = `linear-gradient(
      165deg,
      rgba(2, 4, 12, 0.92) 0%,
      rgba(6, 12, 28, 0.88) 35%,
      rgba(3, 6, 18, 0.94) 100%
    ), url("${treydorexSalesBase}/01_trdx_section_background_city.webp")`;

  const inventoryLine = sale.loading
    ? "Loading…"
    : formatSaleInventoryLabel(sale.inventoryWei);

  const ethRateLine = sale.loading
    ? "Rate loading…"
    : formatEthRateLabel(sale.tokensPerEthWei);

  const usdcPriceLine = sale.loading
    ? "Price loading…"
    : formatUsdcPriceLabel(sale.pricePerTrdxUsdcWei);

  const statusBadgeClass =
    sale.publicStatus === "live"
      ? "trdx-access-section__status trdx-access-section__status--live"
      : "trdx-access-section__status trdx-access-section__status--pending";

  const eyebrow =
    sale.publicStatus === "live" ? "Wallet sale live" : "Treydorex access";

  const handleFundWallet = () => {
    fundWallet.start();
  };

  const handleBuyToken = () => {
    trackTrdxBuyClick({
      cta_id: "trdx_access_buy_token",
      section: "treydorex_access",
    });
    setPurchaseOpen(true);
  };

  const handleFundFromModal = () => {
    setPurchaseOpen(false);
    fundWallet.start();
  };

  return (
    <section id="sales" className="trdx-access-section" aria-labelledby="trdx-access-heading">
      <div
        className="trdx-access-section__bg"
        aria-hidden
        style={{ backgroundImage: sectionBgLayers }}
      />
      <div className="trdx-access-section__inner">
        <div className="trdx-access-section__grid">
          <div className="trdx-access-section__left">
            <p className="trdx-access-section__eyebrow">{eyebrow}</p>
            <h2 id="trdx-access-heading" className="trdx-access-section__headline">
              Enter Oshar.
              <br />
              Enter Treydora.
            </h2>
            <p className="trdx-access-section__body">
              Treydorex is the utility token access path into the Treydora game ecosystem and Oshar VR City.
            </p>
            <p className="trdx-access-section__body trdx-access-section__body--tight">
              Hold 100 TRDX to unlock future token-gated game access (not required for dashboard sign-in).
            </p>
            <div className="trdx-access-section__features">
              <div className="trdx-access-section__feature">
                <img
                  src={`${treydorexSalesBase}/05_access_icon_oshar_city.webp`}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                <div>
                  <h3>Oshar VR City</h3>
                  <p>Utility and access tied to the Oshar VR City experience.</p>
                </div>
              </div>
              <div className="trdx-access-section__feature">
                <img
                  src={`${treydorexSalesBase}/06_access_icon_game.webp`}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                <div>
                  <h3>Treydora game ecosystem</h3>
                  <p>Token-gated access and participation across Treydora: Rise of Koraxos.</p>
                </div>
              </div>
              <div className="trdx-access-section__feature">
                <img
                  src={`${treydorexSalesBase}/07_access_icon_holder_benefits.webp`}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                <div>
                  <h3>Ecosystem participation</h3>
                  <p>Holder utility and ecosystem features for active participants.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="trdx-access-section__center">
            <div className="trdx-access-section__poster-frame">
              <div className="trdx-access-section__poster-inner">
                <img
                  src={`${treydorexSalesBase}/02_game_poster_koraxos_no_text.webp`}
                  alt="Treydora: Rise of Koraxos artwork"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
            <p className="trdx-access-section__poster-caption">Treydora: Rise of Koraxos</p>
          </div>

          <div className="trdx-access-section__panel">
            <h3 className="trdx-access-section__panel-title">{PANEL_TITLE}</h3>
            <p className="trdx-access-section__panel-sub">
              Utility token for access, participation, and ecosystem features across Treydora and Oshar.
            </p>
            <div className="trdx-access-section__sale-status" aria-live="polite">
              <p>
                <span className="trdx-access-section__sale-status-label">Sale contract</span>
                <span className="trdx-access-section__sale-status-value">{getTrdxNetworkLabel()}</span>
              </p>
              <p>
                <span className="trdx-access-section__sale-status-label">Inventory</span>
                <span className="trdx-access-section__sale-status-value">{inventoryLine}</span>
              </p>
              <p>
                <span className="trdx-access-section__sale-status-label">Price</span>
                <span className="trdx-access-section__sale-status-value">{usdcPriceLine}</span>
              </p>
              <p>
                <span className="trdx-access-section__sale-status-label">ETH rate</span>
                <span className="trdx-access-section__sale-status-value">{ethRateLine}</span>
              </p>
            </div>
            <div className="trdx-access-section__coin-wrap">
              <img
                src={`${treydorexSalesBase}/03_trdx_coin_primary.webp`}
                alt=""
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="trdx-access-section__cta-row">
              <button
                type="button"
                className="trdx-access-section__btn trdx-access-section__btn--gold"
                onClick={handleFundWallet}
                disabled={fundWallet.busy}
              >
                {fundWallet.busy ? fundWallet.statusMessage || "Please wait…" : "Fund Wallet with ETH"}
              </button>
              <button
                type="button"
                className="trdx-access-section__btn trdx-access-section__btn--blue"
                onClick={handleBuyToken}
              >
                Buy TREYDOREX Token
              </button>
            </div>

            <p className="trdx-access-section__cta-note">
              Funding adds ETH to your connected wallet. TREYDOREX purchases complete on-chain through your wallet.
            </p>

            {fundWallet.statusMessage && fundWallet.busy ? (
              <p className="trdx-access-section__fund-status" role="status">
                {fundWallet.statusMessage}
              </p>
            ) : null}
            {fundWallet.error ? (
              <p className="trdx-access-section__fund-error" role="alert">
                {fundWallet.error}
              </p>
            ) : null}

            <p className="trdx-access-section__footer">
              <span>
                Configured for <strong>{getTrdxNetworkLabel()}</strong>
              </span>
              <span className={statusBadgeClass} aria-label={`Sale status: ${sale.statusLabel}`}>
                {sale.statusLabel}
              </span>
            </p>
          </div>
        </div>
      </div>

      <TrdxPurchaseModal
        isOpen={purchaseOpen}
        onClose={() => setPurchaseOpen(false)}
        saleSnapshot={sale}
        onFundWalletEth={handleFundFromModal}
      />
    </section>
  );
};

export default TreydorexAccessSection;
